import React, { useState, useEffect } from 'react';
import { Button, ConfigProvider, Modal, Segmented } from 'antd';
import { CameraOutlined } from '@ant-design/icons';
import { useAppData } from './context/DataContext';
import Scanner from "./Scanner";
import axios from 'axios';
import { ReactComponent as CloseIcon } from './CloseIcon.svg';

// Add import for tickets.service.js
import { getEventTickets } from './tickets.service';

const TicketScanner = ({ info, registeredIndex, fromQR = false, qrUserId = null, isOpen, onOpen, onClose  }) => {
    const [attendeeFilter, setAttendeeFilter] = useState('All');
    const [message, setMessage] = useState('');
    const [error, setError] = useState(''); //Types: "", "Already Checked In", "Invalid Ticket", "User Not Found"
    

    const [checkedInAttendees, setCheckedInAttendees] = useState(info.checkedInAttendees[registeredIndex] || [])
    const attendees = info.requireApproval ? info.approvedAttendees[registeredIndex] : info.registered[registeredIndex]

    const { users } = useAppData();

    // Add state for storing tickets
    const [eventTickets, setEventTickets] = useState([]);
    const [loading, setLoading] = useState(false);

    // Add state for expanded tickets
    const [expandedEmail, setExpandedEmail] = useState(null);

    // Add useEffect to fetch tickets when the modal is opened
    useEffect(() => {
        const fetchEventTickets = async () => {
            if (!isOpen) return;
            
            try {
                setLoading(true);
                const tickets = await getEventTickets(info.event_id);
                setEventTickets(tickets || []);
                setLoading(false);
            } catch (error) {
                console.error("Error fetching event tickets:", error);
                setLoading(false);
            }
        };

        fetchEventTickets();
    }, [isOpen, info.event_id]);

    // Add a refresh function
    const refreshTickets = async () => {
        try {
            console.log("Refreshing tickets...");
            setLoading(true);
            
            // Fetch the latest tickets from the server
            const tickets = await getEventTickets(info.event_id);
            console.log("Fetched tickets:", tickets);
            
            // Force a re-render by creating a new array
            setEventTickets([...(tickets || [])]);
            
            // Also refresh the legacy checkedInAttendees
            try {
                const eventResponse = await axios.get(`${process.env.REACT_APP_SERVER_URL}events/${info.event_id}`, {
                    headers: {
                        'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY
                    }
                });
                
                if (eventResponse.data && eventResponse.data.data) {
                    const eventData = eventResponse.data.data;
                    if (eventData.checkedInAttendees && eventData.checkedInAttendees[registeredIndex]) {
                        console.log("Updated checkedInAttendees from server:", eventData.checkedInAttendees[registeredIndex]);
                        setCheckedInAttendees([...eventData.checkedInAttendees[registeredIndex]]);
                        
                        // Update the local info object
                        info.checkedInAttendees[registeredIndex] = [...eventData.checkedInAttendees[registeredIndex]];
                    }
                }
            } catch (error) {
                console.error("Error refreshing legacy check-in data:", error);
            }
            
            setLoading(false);
            console.log("Refresh complete");
        } catch (error) {
            console.error("Error refreshing tickets:", error);
            setLoading(false);
        }
    };

    function getNameFromEmail(inputEmail) {
        const userIndex = users.findIndex(user => user.email === inputEmail);
        return userIndex !== -1 ? users[userIndex].username : inputEmail;
    }

    const handleOpenModal = () => {
        onOpen();
    };

    const handleCloseModal = () => {
        onClose();
    };

    const renderAttendeesList = () => {
        if (loading) {
            return <div className="loading">Loading attendees...</div>;
        }

        // Check if we have tickets from the new system
        if (eventTickets && eventTickets.length > 0) {
            // Group tickets by email
            const ticketsByEmail = eventTickets.reduce((acc, ticket) => {
                if (!acc[ticket.email]) {
                    acc[ticket.email] = [];
                }
                acc[ticket.email].push(ticket);
                return acc;
            }, {});

            // Filter based on attendeeFilter
            const filteredEmails = Object.keys(ticketsByEmail).filter(email => {
                const tickets = ticketsByEmail[email];
                if (attendeeFilter === 'Checked In') {
                    return tickets.some(ticket => ticket.isUsed);
                }
                if (attendeeFilter === 'Pending') {
                    return tickets.some(ticket => !ticket.isUsed);
                }
                return true;
            });

            if (filteredEmails.length === 0) {
                return (
                    <div className="no-attendees">
                        <p className="no-attendees-text">No attendees match the current filter</p>
                    </div>
                );
            }

            // Create a unique key for the attendee list based on the tickets data
            const attendeeListKey = `attendee-list-${eventTickets.length}-${JSON.stringify(
                filteredEmails.map(email => {
                    const tickets = ticketsByEmail[email];
                    const usedCount = tickets.filter(t => t.isUsed).length;
                    return `${email}-${usedCount}`;
                })
            )}`;
            
            return (
                <div className="attendees-list" key={attendeeListKey}>
                    {filteredEmails.map(email => {
                        const tickets = ticketsByEmail[email];
                        const usedTickets = tickets.filter(ticket => ticket.isUsed);
                        
                        // Determine background color based on ticket status
                        let bgColor = 'white';
                        if (usedTickets.length === tickets.length) {
                            bgColor = '#e6ffe6'; // All tickets used - green
                        } else if (usedTickets.length > 0) {
                            bgColor = '#fff9e6'; // Some tickets used - light yellow
                        }
                        
                        return (
                            <div
                                key={`attendee-${email}-${usedTickets.length}-${Date.now()}`}
                                className="attendee-card"
                                onClick={() => toggleExpand(email)}
                            >
                                <div style={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                                    <div className="attendee-avatar">
                                        <span className="avatar-text">
                                            {getNameFromEmail(email)?.charAt(0).toUpperCase()}
                                        </span>
                                    </div>
                                    <div className="name-email-div" style={{ flex: 1 }}>
                                        <span><b>{getNameFromEmail(email)}</b></span>
                                        <span>{email}</span>
                                        <span className="ticket-count">
                                            Tickets: {usedTickets.length}/{tickets.length} used
                                            {tickets.length > 1 && (
                                                <span style={{ marginLeft: '8px', fontSize: '0.8rem', color: '#1890ff' }}>
                                                    {expandedEmail === email ? '(Click to collapse)' : '(Click to expand)'}
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                </div>
                                
                                {/* Show individual tickets when expanded */}
                                {expandedEmail === email && tickets.length > 1 && (
                                    <div style={{ marginTop: '12px', paddingLeft: '52px' }}>
                                        {/* <div style={{ fontSize: '0.9rem', marginBottom: '8px', fontWeight: 'bold' }}>
                                            Individual Tickets:
                                        </div> */}
                                        {tickets.map((ticket, idx) => (
                                            <div 
                                                key={ticket.ticketId} 
                                                style={{ 
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    padding: '8px',
                                                    backgroundColor: ticket.isUsed ? 'rgba(230, 255, 230, 0.075)' : 'rgba(255, 255, 255, 0.075)',
                                                    border: 'solid 1px rgba(255, 255, 255, 0.075)',
                                                    backdropFilter: 'blur(10px)',
                                                    WebkitBackdropFilter: 'blur(10px)',
                                                    borderRadius: '4px',
                                                    marginBottom: '4px'
                                                }}
                                            >
                                                <div>Ticket #{idx + 1}</div>
                                                <div style={{ 
                                                    color: ticket.isUsed ? 'green' : '#666',
                                                    fontWeight: ticket.isUsed ? 'bold' : 'normal'
                                                }}>
                                                    {ticket.isUsed ? 'Used' : 'Not Used'}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            );
        }

        // Fall back to legacy attendee list if no tickets found
        if (!attendees || attendees.length === 0) {
            return (
                <div className="no-attendees">
                    <p className="no-attendees-text">{`No attendees ${info.requireApproval ? 'approved' : 'registered'} yet`}</p>
                </div>
            );
        }

        const filteredAttendees = attendees.filter(email => {
            if (attendeeFilter === 'Checked In') {
                return checkedInAttendees.includes(email);
            }
            if (attendeeFilter === 'Pending') {
                return !checkedInAttendees.includes(email);
            }

            // if attendeeFilter is 'All' or any other value
            return true;
        });

        // Create a unique key for the legacy attendee list
        const legacyAttendeeListKey = `legacy-attendee-list-${checkedInAttendees.length}-${Date.now()}`;
        
        return (
            <div className="attendees-list" key={legacyAttendeeListKey}>
                {filteredAttendees.map((email, index) => (
                    <div
                        key={`attendee-${email}-${checkedInAttendees.includes(email)}-${Date.now()}`}
                        className="attendee-card"
                        style={{ backgroundColor: checkedInAttendees.includes(email) ? '#e6ffe6' : 'white' }}
                    >
                        <div style={{ display: 'flex', width: '100%' }}>
                            <div className="attendee-avatar">
                                <span className="avatar-text">
                                    {getNameFromEmail(email)?.charAt(0).toUpperCase()}
                                </span>
                            </div>
                            <div className="name-email-div" style={{ flex: 1 }}>
                                <span><b>{getNameFromEmail(email)}</b></span>
                                <span>{email}</span>
                                <span className="ticket-count">
                                    Status: {checkedInAttendees.includes(email) ? 'Checked In' : 'Not Checked In'}
                                </span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const scannerCallback = async (result, event_id, err) => {
        // Handle error cases
        if (err) {
            setError(err);
            setMessage(err === "Invalid Ticket" ? 'Invalid Ticket' : 
                      (result && result.email ? `${result.email} is not registered/approved` : err));
            return;
        }

        // If no result or not in the expected format, show an error
        if (!result || typeof result !== 'object' || !result.email || !result.ticketId) {
            console.error("Invalid scan result format:", result);
            setError("Invalid Ticket");
            setMessage("Invalid ticket format");
            return;
        }

        // Extract ticket data
        const { email, ticketId } = result;
        
        console.log("Processing ticket:", { email, ticketId });
        
        try {
            // The ticket has already been verified in Scanner.js
            // Just update the UI
            setError(false);
            setMessage(`${email} has been checked in.`);
            
            // Force a refresh of the tickets to show the updated status
            console.log("Refreshing tickets to update UI...");
            await refreshTickets();
            
            // Also update legacy checkedInAttendees for backward compatibility
            const currentAttendees = await new Promise(resolve => {
                setCheckedInAttendees(prev => {
                    resolve(prev);
                    return prev;
                });
            });
            
            if (!currentAttendees.includes(email)) {
                console.log("Adding email to checked-in attendees:", email);
                
                // Create a new array with the new attendee
                const newCheckedInAttendees = [email, ...currentAttendees];
                
                // Update the database structure
                const updatedCheckedInAttendees = [...info.checkedInAttendees];
                updatedCheckedInAttendees[registeredIndex] = newCheckedInAttendees;
                
                // Send the update to the server
                await axios.put(`${process.env.REACT_APP_SERVER_URL}events/${event_id}/`, {
                    checkedInAttendees: updatedCheckedInAttendees
                }, {
                    headers: {
                        'x-api-key': process.env.REACT_APP_UMMAHFY_API_KEY
                    }
                });
                
                console.log("Updated checkedInAttendees in database");
                
                // Update the local state with the new attendee list
                setCheckedInAttendees(newCheckedInAttendees);
                
                // Also update the info object to keep it in sync
                info.checkedInAttendees[registeredIndex] = newCheckedInAttendees;
            }
            
            // Force another refresh after a short delay to ensure UI is updated
            setTimeout(async () => {
                console.log("Performing delayed refresh...");
                await refreshTickets();
            }, 1000);
            
        } catch (error) {
            console.error("Error processing ticket:", error);
            setError("Processing Failed");
            setMessage("Failed to process ticket. Please try again.");
        }
    }

    // Add a function to toggle expanded state
    const toggleExpand = (email) => {
        setExpandedEmail(expandedEmail === email ? null : email);
    };

    return (
        <>

                <Button
                    
                    icon={<CameraOutlined />}
                    onClick={handleOpenModal}
                    
                    className="scan-ticket-button"

                >
                    Scan Ticket
                </Button>


            <ConfigProvider
                theme={{
                    components: {
                        Modal: {
                            titleFontSize: 20,
                        },
                    },
                }}
            >
                <Modal
                    title="Scan Ticket"
                    open={isOpen}
                    onCancel={handleCloseModal}
                    width={800}
                    style={{ top: 20 }}
                    bodyStyle={{
                        maxHeight: 'calc(100vh - 225px)',
                        overflow: 'auto',
                    }}
                    footer={[
                        // <Button key="close" onClick={handleCloseModal}>
                        //     Close
                        // </Button>
                    ]}
                >
                    <div className="scanner-container">
                        <div className="scanner-view">
                            

                            <Scanner
                                event_id={(info.event_id).toString()}
                                onScanComplete={scannerCallback}
                                attendees={attendees}
                                checkedInAttendees={checkedInAttendees}
                                fromQR={fromQR}
                                qrUserId={qrUserId}
                                className="scanner"
                                // modalOpen getting passed over is not consistently checking if its open
                                modalOpen={isOpen}
                            />

                            {message && (
                                <div className="scanner-response-field" style={{
                                    border: `2px solid ${!error ? 'green' : error === 'Already Checked In' ? '#FFB800' : 'red'}`,
                                    borderRadius: '8px',
                                }}>
                                    <p>{message}</p>
                                    <CloseIcon className="close-icon" onClick={() => {
                                        setMessage('')
                                        setError('')
                                    }} />
                                </div>
                            )}

                        </div>

                        <div className="registered-attendees">
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '24px'
                            }}>
                                <h3 style={{ margin: 0 }}>Attendees</h3>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Button 
                                        size="small" 
                                        onClick={refreshTickets} 
                                        loading={loading}
                                    >
                                        Refresh
                                    </Button>
                                    <Segmented
                                        className="org-toggle"
                                        options={['All', 'Checked In', 'Pending']}
                                        value={attendeeFilter}
                                        onChange={setAttendeeFilter}
                                    />
                                </div>
                            </div>
                            {renderAttendeesList()}
                        </div>
                    </div>
                </Modal>
            </ConfigProvider>
        </>
    );
};

export default TicketScanner;